import { Card, useMantineTheme } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { IResourceSection } from '../../models/IStatistic';
import {
  ArcElement,
  Chart as ChartJS,
  type ChartOptions,
  Legend,
  Tooltip,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { STATISTICS_STATUS_COLOR_ENUM } from '../../api_enums/STATISTICS_STATUS_ENUM';

ChartJS.register(ArcElement, Tooltip, Legend);

interface Prop {
  chartData: IResourceSection[];
}

function StatsPie({ chartData }: Readonly<Prop>) {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  const GetResourceInfo = (queryResult: IResourceSection[] = []) => {
    const resourceStatusMap = {};
    let total = 0;

    queryResult?.forEach(({ status, count }) => {
      resourceStatusMap[status] =
        (resourceStatusMap[status] ?? 0) + Number(count);
      total += Number(count);
    });

    const status = Object.keys(resourceStatusMap);
    const labels = status.map(
      (state) => t(`StatisticsHelp.${state}`) || 'Unknown Status',
    );
    return {
      pieData: {
        labels,
        datasets: [
          {
            data: status.map((state) => resourceStatusMap[state]),
            backgroundColor: status.map(
              (state) => STATISTICS_STATUS_COLOR_ENUM[state],
            ),
          },
        ],
      },
      total,
    };
  };

  const resourceData = GetResourceInfo(chartData);
  const pieOptions: ChartOptions<'pie'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: `${t('StatsControlsBarchart.Total')}: ${resourceData.total}`,
        font: {
          family: `Greycliff CF, ${theme.fontFamily}`,
          size: theme.fontSizes.sm,
          weight: 'bold',
        },
      },
      legend: {
        position: 'bottom',
        labels: {
          font: {
            family: `Greycliff CF, ${theme.fontFamily}`,
            size: theme.fontSizes.xs,
          },
        },
        display: false,
      },
    },
  };
  const ref = React.useRef(null);
  ref.current?.update();
  return (
    <Card withBorder p="xl" radius="md" sx={{ height: '100%' }}>
      <Pie ref={ref} options={pieOptions} data={resourceData.pieData} />
    </Card>
  );
}

export default StatsPie;
